import React from "react";
import { motion } from "framer-motion";
import { useTheme } from "../context/ThemeContext";

const BenefitCard = ({ icon, title, description }) => {
  const { isDarkMode } = useTheme();
  return (
    <div className="flex flex-col items-center text-center">
      <div className="w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 mb-4">
        {typeof icon === "string" ? (
          <img
            src={icon}
            alt={title}
            className="w-full h-full object-contain"
          />
        ) : (
          // If icon is an SVG element
          <div className="w-full h-full">{icon}</div>
        )}
      </div>
      <p className="text-[14px] sm:text-[15px] md:text-[18px] font-lexend max-w-[280px] sm:max-w-[200px] md:max-w-[280px] mx-auto">
        <span
          className={`font-bold ${isDarkMode ? "text-[#fff]" : "text-[#000]"}`}
        >
          {title}
        </span>
        <span className={`${isDarkMode ? "text-[#fff]" : "text-[#000]"}`}>: </span>
        <span className={`font-lexend font-light ${isDarkMode ? "text-[#fff]" : "text-[#000]"}`}>
          {description}
        </span>
      </p>
    </div>
  );
};

const BenefitsSection = () => {
  const { isDarkMode } = useTheme();
  const edgeColor = isDarkMode ? "#000" : "#fff";

  // Gradient styles
  const verticalGradientStyle = {
    background: `linear-gradient(to bottom, ${edgeColor}, #FCD535, ${edgeColor})`,
  };

  const horizontalGradientStyle = {
    background: `linear-gradient(to right, ${edgeColor}, #FCD535, ${edgeColor})`,
  };

  const benefits = [
    {
      title: "Elevate Your Skills",
      description:
        " Tackle cutting-edge AI challenges that push your abilities and sharpen your expertise.",
      icon: (
        <svg
          width="36"
          height="36"
          viewBox="0 0 36 36"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M6 30C6.8295 30 7.5 30.672 7.5 31.5C7.5 32.3265 8.172 33 9 33H19.5C20.328 33 21 32.3265 21 31.5V30C21 29.172 21.6705 28.5 22.5 28.5H24.741C26.9895 28.5 28.9065 26.8185 29.2035 24.5895L29.7045 20.8035C29.802 20.058 30.438 19.5 31.191 19.5C32.154 19.5191 32.7948 18.7159 32.4257 17.8262C31.3125 15.1427 28.8475 10.3412 27.2985 8.4255C24.7155 4.977 20.7795 2.9985 16.5 2.9985C15.6705 2.9985 15 2.3265 15 1.4985C15 0.672 15.6705 0 16.5 0C21.7335 0 26.544 2.415 29.6655 6.5835C31.602 8.979 36 17.37 36 19.5C36 20.955 34.9485 22.5 33 22.5C32.7167 22.5 32.4772 22.7099 32.4401 22.9907L32.1765 24.984C31.6845 28.6995 28.488 31.5 24.741 31.5C24.3318 31.5 24.0064 31.8342 23.9395 32.2379C23.5863 34.3692 21.7297 36 19.5 36H9C6.519 36 4.5 33.981 4.5 31.5C4.5 30.672 5.1705 30 6 30ZM3.75 15H8.25C10.3185 15 12 16.683 12 18.75V23.25C12 25.317 10.3185 27 8.25 27H3.75C1.6815 27 0 25.317 0 23.25V18.75C0 16.683 1.6815 15 3.75 15ZM3 23.25C3 23.664 3.3375 24 3.75 24H8.25C8.6625 24 9 23.664 9 23.25V18.75C9 18.336 8.6625 18 8.25 18H3.75C3.3375 18 3 18.336 3 18.75V23.25ZM12 3V9C12 10.6575 10.6575 12 9 12H3C1.3425 12 0 10.6575 0 9V3C0 1.3425 1.3425 0 3 0H9C10.6575 0 12 1.3425 12 3ZM10.0732 3.16425C10.0728 3.16466 10.0722 3.16465 10.0718 3.16424C9.48068 2.5605 8.50925 2.56376 7.923 3.171L6.35915 4.7896C5.71718 5.45405 4.65058 5.44854 4.0155 4.7775C3.4275 4.1565 2.4405 4.152 1.848 4.7685C1.293 5.3445 1.29 6.2565 1.839 6.837L2.9925 8.0565C4.173 9.3045 6.156 9.315 7.35 8.079L10.08 5.2545C10.6438 4.67126 10.641 3.74481 10.0747 3.16426C10.0743 3.16385 10.0737 3.16384 10.0732 3.16425Z"
            fill="url(#paint0_linear_585_748)"
          />
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M12 3V9C12 10.6575 10.6575 12 9 12H3C1.3425 12 0 10.6575 0 9V3C0 1.3425 1.3425 0 3 0H9C10.6575 0 12 1.3425 12 3ZM10.0732 3.16425C10.0728 3.16466 10.0722 3.16465 10.0718 3.16424C9.48068 2.5605 8.50925 2.56376 7.923 3.171L6.35915 4.7896C5.71718 5.45405 4.65058 5.44854 4.0155 4.7775C3.4275 4.1565 2.4405 4.152 1.848 4.7685C1.293 5.3445 1.29 6.2565 1.839 6.837L2.9925 8.0565C4.173 9.3045 6.156 9.315 7.35 8.079L10.08 5.2545C10.6438 4.67126 10.641 3.74481 10.0747 3.16426C10.0743 3.16385 10.0737 3.16384 10.0732 3.16425Z"
            fill="url(#paint1_linear_585_748)"
          />
          <defs>
            <linearGradient
              id="paint0_linear_585_748"
              x1="36"
              y1="36"
              x2="-3.8965"
              y2="30.0542"
              gradientUnits="userSpaceOnUse"
            >
              <stop stopColor="#FCD535" />
              <stop offset="1" stopColor="#FF636E" />
            </linearGradient>
            <linearGradient
              id="paint1_linear_585_748"
              x1="12"
              y1="12"
              x2="-1.29883"
              y2="10.0181"
              gradientUnits="userSpaceOnUse"
            >
              <stop stopColor="#FCD535" />
              <stop offset="1" stopColor="#FF636E" />
            </linearGradient>
          </defs>
        </svg>
      ),
    },
    {
      title: "Build Your Portfolio",
      description:
        "Develop real-world AI solutions, showcasing your achievements to future employers and collaborators.",
      icon: (
        <svg
          width="36"
          height="36"
          viewBox="0 0 36 36"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M13.6727 29.9988C13.673 29.9991 13.6728 29.9995 13.6724 29.9995H11.0954C10.8395 29.9995 10.6332 30.2087 10.6352 30.4646C10.6352 30.4658 10.6352 30.4652 10.6352 30.4664C10.6362 31.8637 11.7873 32.9998 13.2025 32.9998H15.1926C16.0312 32.9998 16.7118 33.6718 16.7118 34.4999C16.7118 35.3279 16.0312 36 15.1926 36H13.2025C10.111 36 7.59684 33.5173 7.59684 30.4646V30.2231C7.59684 27.7239 6.44684 25.2907 4.35801 23.372C1.09334 20.3733 -0.451638 16.1295 0.115007 11.7311C0.897372 5.67068 5.93185 0.772802 12.0875 0.0857488C18.8492 -0.670309 25.2282 3.64702 26.9159 10.1245C27.1256 10.9271 26.6349 11.7446 25.8221 11.9502C25.0215 12.1587 24.1815 11.6756 23.9718 10.8716C22.6395 5.75168 17.7797 2.47093 12.4278 3.06648C7.64698 3.59902 3.73667 7.40331 3.12901 12.1107C2.68542 15.5384 3.88859 18.8432 6.42558 21.1759C8.01802 22.6396 9.19083 24.3517 9.8873 26.1912C10.0662 26.6637 10.506 26.9978 11.0112 26.9978C11.6417 26.9978 12.1528 26.4867 12.1528 25.8562V20.7033C12.1528 18.0292 9.90422 15.6781 9.24933 13.0855C9.16125 12.7368 9.11448 12.372 9.11448 11.9967C9.11448 11.1686 9.79506 10.4965 10.6336 10.4965C11.4722 10.4965 12.1528 11.1686 12.1528 11.9967C12.1528 12.8247 12.8349 13.4968 13.6719 13.4968C14.509 13.4968 15.1911 12.8232 15.1911 11.9967C15.1911 11.1701 15.8717 10.4965 16.7103 10.4965C17.5488 10.4965 18.2294 11.1686 18.2294 11.9967C18.2294 15.3295 15.1911 18.4703 15.1911 21.8031V28.4979C15.1911 29.3256 14.5111 29.9975 13.673 29.998C13.6726 29.998 13.6724 29.9985 13.6727 29.9988ZM35.7954 30.7496C35.5128 31.2311 35.0039 31.4997 34.4783 31.4997C34.22 31.4997 33.9587 31.4352 33.7202 31.2986C32.8725 30.8153 31.8179 31.0152 31.0235 31.582C30.6044 31.881 30.1535 32.1389 29.6766 32.3496C28.798 32.7379 28.1054 33.5393 28.1054 34.4999C28.1054 35.3279 27.4264 36 26.5863 36C25.7462 36 25.0671 35.3279 25.0671 34.4999C25.0671 33.5393 24.3746 32.7379 23.4959 32.3498C23.0189 32.1391 22.5678 31.8812 22.1488 31.5823C21.3545 31.0156 20.2999 30.8154 19.4523 31.2986C19.2123 31.4352 18.951 31.4997 18.6943 31.4997C18.1686 31.4997 17.6582 31.2311 17.3772 30.7496C16.9579 30.031 17.207 29.1145 17.9332 28.7004C18.7737 28.2215 19.1252 27.213 19.0281 26.2505C19.0032 26.0038 18.9905 25.7537 18.9905 25.5007C18.9905 25.2478 19.0034 24.9977 19.0284 24.751C19.126 23.7883 18.7739 22.7801 17.9332 22.301C17.207 21.8869 16.9579 20.9688 17.3772 20.2518C17.7964 19.5347 18.7231 19.2872 19.4523 19.7028C20.3 20.1861 21.3547 19.9862 22.1491 19.4194C22.5681 19.1204 23.0191 18.8625 23.4959 18.6518C24.3745 18.2635 25.0671 17.4621 25.0671 16.5015C25.0671 15.6734 25.7462 15.0014 26.5863 15.0014C27.4264 15.0014 28.1054 15.6734 28.1054 16.5015C28.1054 17.4621 28.798 18.2635 29.6767 18.6516C30.1536 18.8623 30.6047 19.1202 31.0238 19.4191C31.8181 19.9857 32.8726 20.186 33.7202 19.7028C34.4449 19.2887 35.3746 19.5347 35.7954 20.2518C36.2147 20.9703 35.9655 21.8869 35.2394 22.301C34.3989 22.7799 34.0473 23.7884 34.1445 24.7509C34.1693 24.9975 34.1821 25.2477 34.1821 25.5007C34.1821 25.7536 34.1692 26.0037 34.1442 26.2504C34.0466 27.2131 34.3987 28.2213 35.2394 28.7004C35.9671 29.1145 36.2162 30.0325 35.7954 30.7496ZM31.1437 25.4992C31.1437 23.018 29.099 20.9988 26.5863 20.9988C24.0736 20.9988 22.0288 23.018 22.0288 25.4992C22.0288 27.9804 24.0736 29.9995 26.5863 29.9995C29.099 29.9995 31.1437 27.9804 31.1437 25.4992Z"
            fill="url(#paint0_linear_585_761)"
          />
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M34.3051 31.4986C34.8307 31.4986 35.3396 31.2301 35.6222 30.7486C36.043 30.0315 35.7939 29.1134 35.0662 28.6994C34.2255 28.2203 33.8734 27.2121 33.971 26.2493C33.996 26.0027 34.0089 25.7526 34.0089 25.4997C34.0089 25.2466 33.9961 24.9965 33.9713 24.7499C33.8741 23.7874 34.2257 22.7789 35.0662 22.2999C35.7923 21.8859 36.0415 20.9693 35.6222 20.2508C35.2014 19.5337 34.2717 19.2877 33.547 19.7017C32.6994 20.185 31.6449 19.9847 30.8506 19.4181C30.4315 19.1191 29.9804 18.8613 29.5035 18.6506C28.6248 18.2625 27.9322 17.4611 27.9322 16.5005C27.9322 15.6724 27.2532 15.0004 26.4131 15.0004C25.573 15.0004 24.8939 15.6724 24.8939 16.5005C24.8939 17.4611 24.2014 18.2625 23.3227 18.6507C22.8459 18.8614 22.3949 19.1193 21.9759 19.4184C21.1815 19.9852 20.1268 20.1851 19.2791 19.7017C18.5499 19.2862 17.6233 19.5337 17.204 20.2508C16.7847 20.9678 17.0338 21.8859 17.76 22.2999C18.6007 22.779 18.9528 23.7873 18.8552 24.75C18.8302 24.9967 18.8173 25.2468 18.8173 25.4997C18.8173 25.7527 18.83 26.0028 18.8549 26.2494C18.952 27.2119 18.6005 28.2204 17.76 28.6994C17.0338 29.1134 16.7847 30.03 17.204 30.7486C17.485 31.2301 17.9954 31.4986 18.5211 31.4986C18.7778 31.4986 19.0391 31.4341 19.2791 31.2976C20.1267 30.8143 21.1813 31.0146 21.9756 31.5813C22.3946 31.8802 22.8457 32.1381 23.3227 32.3487C24.2014 32.7369 24.8939 33.5383 24.8939 34.4989C24.8939 35.3269 25.573 35.999 26.4131 35.999C27.2532 35.999 27.9322 35.3269 27.9322 34.4989C27.9322 33.5383 28.6248 32.7369 29.5034 32.3486C29.9803 32.1379 30.4312 31.88 30.8503 31.581C31.6447 31.0142 32.6993 30.8143 33.547 31.2976C33.7855 31.4341 34.0468 31.4986 34.3051 31.4986ZM26.4131 20.9978C28.9258 20.9978 30.9705 23.017 30.9705 25.4982C30.9705 27.9794 28.9258 29.9985 26.4131 29.9985C23.9004 29.9985 21.8556 27.9794 21.8556 25.4982C21.8556 23.017 23.9004 20.9978 26.4131 20.9978Z"
            fill="url(#paint1_linear_585_761)"
          />
          <defs>
            <linearGradient
              id="paint0_linear_585_761"
              x1="36"
              y1="36"
              x2="-3.8965"
              y2="30.0542"
              gradientUnits="userSpaceOnUse"
            >
              <stop stopColor="#FCD535" />
              <stop offset="1" stopColor="#FF636E" />
            </linearGradient>
            <linearGradient
              id="paint1_linear_585_761"
              x1="35.8268"
              y1="35.999"
              x2="14.8734"
              y2="33.1993"
              gradientUnits="userSpaceOnUse"
            >
              <stop stopColor="#FCD535" />
              <stop offset="1" stopColor="#FF636E" />
            </linearGradient>
          </defs>
        </svg>
      ),
    },
    {
      title: "Collaborate Globally",
      description:
        " Connect with and learn from a diverse global network of data scientists, AI experts, and organizations.",
      icon: (
        <svg
          width="36"
          height="36"
          viewBox="0 0 36 36"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M24.3078 17.5598C21.5801 20.2877 21.5801 24.7104 24.3078 27.4384C24.8943 28.0248 24.8943 28.9728 24.3078 29.5593C24.0154 29.8518 23.6314 29.9988 23.2474 29.9988C22.8635 29.9988 22.4795 29.8518 22.1871 29.5593L18.8394 26.2114C17.4229 24.7948 15.5018 23.999 13.4985 23.999C12.6706 23.999 11.9987 23.3285 11.9987 22.4991C11.9987 21.6696 12.6706 20.9991 13.4985 20.9991C15.5018 20.9991 17.4229 20.2033 18.8394 18.7868L22.1871 15.4389C22.7735 14.8524 23.7214 14.8524 24.3078 15.4389C24.8943 16.0253 24.8943 16.9733 24.3078 17.5598ZM5.24943 16.4993C7.3207 16.4993 8.99901 14.8209 8.99901 12.7495C8.99901 10.6781 7.3207 8.99963 5.24943 8.99963C3.17816 8.99963 1.49984 10.6781 1.49984 12.7495C1.49984 14.8209 3.17816 16.4993 5.24943 16.4993ZM30.7466 7.49969C32.8179 7.49969 34.4962 5.82126 34.4962 3.74984C34.4962 1.67843 32.8179 0 30.7466 0C28.6753 0 26.997 1.67843 26.997 3.74984C26.997 5.82126 28.6753 7.49969 30.7466 7.49969ZM30.7466 20.9991C28.6753 20.9991 26.997 22.6776 26.997 24.749C26.997 26.8204 28.6753 28.4988 30.7466 28.4988C32.8179 28.4988 34.4962 26.8204 34.4962 24.749C34.4962 22.6776 32.8179 20.9991 30.7466 20.9991ZM5.24943 18C5.24943 17.9996 5.24909 17.9993 5.24867 17.9993C2.72927 17.9996 0.533899 19.7649 0.0315046 22.1946C-0.136477 23.006 0.383966 23.7995 1.19538 23.969C2.00229 24.128 2.8002 23.615 2.96818 22.8051C3.18566 21.7596 4.14405 21.0006 5.24943 21.0006C6.35481 21.0006 7.3132 21.7596 7.53067 22.8051C7.67766 23.513 8.30159 24.0005 8.99751 24.0005C10.2109 24.0005 10.6353 23.0075 10.4674 22.1961C9.96496 19.7664 7.76959 18.0011 5.25018 18.0008C5.24977 18.0008 5.24943 18.0004 5.24943 18ZM26.694 14.9686C26.6938 14.9685 26.6939 14.9681 26.6942 14.9682C27.499 15.1232 28.296 14.6134 28.4669 13.8039C28.6843 12.7585 29.6427 11.9995 30.7481 11.9995C31.8535 11.9995 32.8119 12.7585 33.0294 13.8039C33.1778 14.5119 33.8018 14.9994 34.4977 14.9994C35.5371 14.9994 36.1355 14.0064 35.9675 13.195C35.4636 10.7651 33.2693 8.99963 30.7496 8.99963C28.2299 8.99963 26.0341 10.7651 25.5317 13.195C25.3653 14.0059 25.8834 14.7988 26.6939 14.969C26.6941 14.9691 26.6943 14.9687 26.694 14.9686ZM30.7481 29.9995C30.7471 29.999 30.746 29.9988 30.7449 29.9988C28.2244 29.9995 26.031 31.7647 25.5287 34.1941C25.3622 35.0055 25.8811 35.799 26.6926 35.9685C27.498 36.126 28.2959 35.6145 28.4669 34.8046C28.6843 33.7591 29.6427 33.0001 30.7481 33.0001C31.8535 33.0001 32.8119 33.7591 33.0294 34.8046C33.1778 35.5125 33.8018 36 34.4977 36C35.6526 36 36.1355 35.007 35.9675 34.1956C35.4637 31.7662 33.2703 30.001 30.7513 30.0002C30.7502 30.0002 30.7491 30 30.7481 29.9995Z"
            fill="url(#paint0_linear_585_755)"
          />
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M8.99901 12.7498C8.99901 14.8213 7.3207 16.4997 5.24943 16.4997C3.17816 16.4997 1.49984 14.8213 1.49984 12.7498C1.49984 10.6784 3.17816 9 5.24943 9C7.3207 9 8.99901 10.6784 8.99901 12.7498ZM0.0315046 22.195C0.533899 19.7653 2.72927 18 5.24867 17.9996C5.24909 17.9996 5.24943 18 5.24943 18.0004C5.24943 18.0008 5.24977 18.0011 5.25018 18.0011C7.76959 18.0015 9.96496 19.7668 10.4674 22.1965C10.6353 23.0079 10.2109 24.0009 8.99751 24.0009C8.30159 24.0009 7.67766 23.5134 7.53067 22.8054C7.3132 21.76 6.35481 21.001 5.24943 21.001C4.14405 21.001 3.18566 21.76 2.96818 22.8054C2.8002 23.6154 2.00229 24.1284 1.19538 23.9694C0.383966 23.7999 -0.136477 23.0064 0.0315046 22.195Z"
            fill="url(#paint1_linear_585_755)"
          />
          <defs>
            <linearGradient
              id="paint0_linear_585_755"
              x1="31.7051"
              y1="38.0499"
              x2="-3.66657"
              y2="33.6574"
              gradientUnits="userSpaceOnUse"
            >
              <stop stopColor="#FCD535" />
              <stop offset="1" stopColor="#FF636E" />
            </linearGradient>
            <linearGradient
              id="paint1_linear_585_755"
              x1="10.5025"
              y1="24.0009"
              x2="-1.26713"
              y2="22.7728"
              gradientUnits="userSpaceOnUse"
            >
              <stop stopColor="#FCD535" />
              <stop offset="1" stopColor="#FF636E" />
            </linearGradient>
          </defs>
        </svg>
      ),
    },
    {
      icon: (
        <svg
          width="38"
          height="38"
          viewBox="0 0 38 38"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M15.8341 28.5C15.8337 28.5 15.8333 28.5004 15.8333 28.5008C15.8334 28.8523 15.8482 29.2001 15.8772 29.5438C15.9653 30.5902 14.6226 31.5278 13.7817 30.8989L5.06672 24.3812C4.51883 23.9714 3.85303 23.75 3.16886 23.75H1.58333C0.708883 23.75 0 23.0411 0 22.1667C0 21.2922 0.708883 20.5833 1.58333 20.5833H3.96032C4.81526 20.5833 5.64724 20.86 6.33194 21.372L15.829 28.4734C15.8327 28.4762 15.8349 28.4806 15.8349 28.4852C15.8349 28.4896 15.8349 28.4951 15.8349 28.4992C15.8349 28.4996 15.8346 28.5 15.8341 28.5ZM33.6237 3.16788C33.6237 3.16714 33.6232 3.16646 33.6226 3.16613L28.9544 0.83125C26.0569 -0.615917 22.3773 -0.0728333 20.026 2.14383C19.4739 2.66375 18.6146 2.67076 18.0541 2.15992L17.9392 2.05517C16.4857 0.729917 14.5999 0 12.5717 0C11.3557 0 10.1365 0.288167 9.04558 0.83125L5.12403 2.79269C4.63233 3.03863 4.0901 3.16667 3.54032 3.16667H1.58333C0.708882 3.16667 0 3.87555 0 4.75C0 5.62445 0.708882 6.33333 1.58333 6.33333H4.14471C4.78849 6.33333 5.42342 6.18337 5.99917 5.89532L10.4595 3.66383C12.0872 2.85 14.3513 3.07167 15.8048 4.39533C16.3054 4.85145 16.3117 5.63711 15.8187 6.10129L12.5416 9.1865C10.8443 10.8838 10.5719 13.5644 11.9114 15.4233C12.7347 16.5664 14.0062 17.2868 15.3963 17.4008C15.5262 17.4103 15.6544 17.4167 15.7827 17.4167C17.0383 17.4167 18.2432 16.9227 19.114 16.0503C20.0319 15.1716 21.4509 15.0863 22.4675 15.8487L23.1328 16.3477C23.4891 16.615 23.946 16.6751 24.3673 16.5307C25.4843 16.148 25.9445 14.4986 24.9999 13.7901L24.4535 13.3804C23.7053 12.8192 23.6392 11.7207 24.3149 11.0738L24.5538 10.8451C25.1855 10.2404 25.2074 9.23806 24.6027 8.60629C23.998 7.97446 22.9956 7.95249 22.3637 8.55722L16.8989 13.7877C16.5712 14.117 16.1136 14.2896 15.6513 14.2468C15.181 14.2088 14.7646 13.9713 14.4812 13.5771C14.0473 12.9738 14.1803 12.0317 14.7472 11.4633L22.1968 4.45075C23.6028 3.12708 25.8067 2.80092 27.5389 3.667L32.001 5.89869C32.5766 6.1866 33.2114 6.3365 33.8551 6.3365H36.4167C37.2911 6.3365 38 5.62762 38 4.75317C38 3.87872 37.2911 3.16983 36.4167 3.16983H33.6256C33.6245 3.16983 33.6237 3.16896 33.6237 3.16788ZM38 28.5C38 33.7393 33.7393 38 28.5 38C23.2607 38 19 33.7393 19 28.5C19 23.2607 23.2607 19 28.5 19C33.7393 19 38 23.2607 38 28.5ZM34.8333 28.5C34.8333 25.0072 31.9928 22.1667 28.5 22.1667C25.0072 22.1667 22.1667 25.0072 22.1667 28.5C22.1667 31.9928 25.0072 34.8333 28.5 34.8333C31.9928 34.8333 34.8333 31.9928 34.8333 28.5ZM28.5749 28.0643C28.0382 28.5912 27.1793 28.5941 26.6391 28.0708C26.0893 27.5383 25.2119 27.5521 24.6791 28.1017L24.5422 28.2429C23.9334 28.871 23.9494 29.8738 24.5778 30.4822L25.8701 31.7332C26.334 32.2018 26.9531 32.4583 27.6117 32.4583C28.2704 32.4583 28.8895 32.2018 29.3439 31.7474L32.4273 28.7208C33.0513 28.1083 33.0608 27.1058 32.4484 26.4816C31.8357 25.8571 30.8327 25.8477 30.2085 26.4606L28.5749 28.0643Z"
            fill="url(#paint0_linear_585_775)"
          />
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M38 28.5C38 33.7393 33.7393 38 28.5 38C23.2607 38 19 33.7393 19 28.5C19 23.2608 23.2607 19 28.5 19C33.7393 19 38 23.2608 38 28.5ZM34.8333 28.5C34.8333 25.0072 31.9928 22.1667 28.5 22.1667C25.0072 22.1667 22.1667 25.0072 22.1667 28.5C22.1667 31.9928 25.0072 34.8333 28.5 34.8333C31.9928 34.8333 34.8333 31.9928 34.8333 28.5ZM28.5749 28.0643C28.0382 28.5912 27.1793 28.5941 26.6391 28.0708C26.0893 27.5383 25.2119 27.5521 24.6791 28.1017L24.5422 28.2429C23.9334 28.871 23.9494 29.8738 24.5778 30.4822L25.8701 31.7332C26.334 32.2018 26.9531 32.4583 27.6117 32.4583C28.2704 32.4583 28.8895 32.2018 29.3439 31.7474L32.4273 28.7208C33.0513 28.1083 33.0608 27.1058 32.4484 26.4816C31.8357 25.8571 30.8327 25.8477 30.2085 26.4606L28.5749 28.0643Z"
            fill="url(#paint1_linear_585_775)"
          />
          <defs>
            <linearGradient
              id="paint0_linear_585_775"
              x1="38"
              y1="38"
              x2="-4.11297"
              y2="31.7239"
              gradientUnits="userSpaceOnUse"
            >
              <stop stopColor="#FCD535" />
              <stop offset="1" stopColor="#FF636E" />
            </linearGradient>
            <linearGradient
              id="paint1_linear_585_775"
              x1="38"
              y1="38"
              x2="16.9435"
              y2="34.8619"
              gradientUnits="userSpaceOnUse"
            >
              <stop stopColor="#FCD535" />
              <stop offset="1" stopColor="#FF636E" />
            </linearGradient>
          </defs>
        </svg>
      ),
      title: "Earn Rewards",
      description:
        " Get recognized for your contributions, receive monetary rewards, and co-own the solutions you help create.",
    },
    {
      icon: (
        <svg
          width="32"
          height="39"
          viewBox="0 0 32 39"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M23.7789 12.6858H9.51154C8.63603 12.6858 7.92628 11.976 7.92628 11.1005C7.9258 9.34309 8.50938 7.63535 9.58528 6.24577C10.6612 4.85619 12.1684 3.86358 13.8699 3.42399C15.5715 2.9844 17.3709 3.12277 18.9852 3.81733C20.5995 4.5119 21.9373 5.72327 22.7881 7.26101C22.8891 7.44306 23.0251 7.60342 23.1881 7.73294C23.3512 7.86246 23.5381 7.95859 23.7383 8.01585C23.9385 8.07312 24.148 8.09039 24.3549 8.06668C24.5618 8.04297 24.762 7.97875 24.944 7.87767C25.1261 7.7766 25.2864 7.64066 25.4159 7.47762C25.5455 7.31457 25.6416 7.12761 25.6989 6.92742C25.7561 6.72722 25.7734 6.5177 25.7497 6.31083C25.726 6.10396 25.6618 5.90378 25.5607 5.72173C24.369 3.56945 22.4959 1.87418 20.2359 0.902408C17.9758 -0.0693632 15.4568 -0.262539 13.075 0.353247C10.6932 0.969033 8.58348 2.35886 7.07758 4.30427C5.57168 6.24968 4.75495 8.64035 4.75577 11.1005C4.75577 12.4716 3.89429 13.6619 2.84237 14.5414C2.24807 15.0382 1.7267 15.6221 1.29753 16.2767C0.452916 17.565 0.00202655 19.0715 0 20.612V30.1236C0.00251716 32.225 0.838414 34.2396 2.32434 35.7255C3.81026 37.2115 5.82488 38.0473 7.92628 38.0499H23.7789C25.8803 38.0473 27.8949 37.2115 29.3808 35.7255C30.8667 34.2396 31.7026 32.225 31.7051 30.1236V20.612C31.7026 18.5106 30.8667 16.496 29.3808 15.0101C27.8949 13.5242 25.8803 12.6883 23.7789 12.6858ZM28.5346 30.1236C28.5346 31.3849 28.0336 32.5945 27.1417 33.4864C26.2498 34.3783 25.0402 34.8794 23.7789 34.8794H7.92628C6.66498 34.8794 5.45533 34.3783 4.56345 33.4864C3.67157 32.5945 3.17051 31.3849 3.17051 30.1236V20.612C3.17051 19.3507 3.67157 18.1411 4.56345 17.2492C5.45533 16.3573 6.66498 15.8563 7.92628 15.8563H23.7789C25.0402 15.8563 26.2498 16.3573 27.1417 17.2492C28.0336 18.1411 28.5346 19.3507 28.5346 20.612V30.1236Z"
            fill="url(#paint0_linear_585_784)"
          />
          <path
            d="M15.8526 22.1973C15.4322 22.1973 15.0289 22.3643 14.7316 22.6616C14.4344 22.9589 14.2673 23.3621 14.2673 23.7825V26.953C14.2673 27.3735 14.4344 27.7767 14.7316 28.074C15.0289 28.3713 15.4322 28.5383 15.8526 28.5383C16.273 28.5383 16.6762 28.3713 16.9735 28.074C17.2708 27.7767 17.4378 27.3735 17.4378 26.953V23.7825C17.4378 23.3621 17.2708 22.9589 16.9735 22.6616C16.6762 22.3643 16.273 22.1973 15.8526 22.1973Z"
            fill="url(#paint1_linear_585_784)"
          />
          <defs>
            <linearGradient
              id="paint0_linear_585_784"
              x1="31.7051"
              y1="38.0499"
              x2="-3.66657"
              y2="33.6574"
              gradientUnits="userSpaceOnUse"
            >
              <stop stopColor="#FCD535" />
              <stop offset="1" stopColor="#FF636E" />
            </linearGradient>
            <linearGradient
              id="paint1_linear_585_784"
              x1="17.4378"
              y1="28.5383"
              x2="13.866"
              y2="28.2721"
              gradientUnits="userSpaceOnUse"
            >
              <stop stopColor="#FCD535" />
              <stop offset="1" stopColor="#FF636E" />
            </linearGradient>
          </defs>
        </svg>
      ),
      title: "Expand Your Professional Network",
      description:
        " Engage with leading organizations and experts, opening doors to new career and project opportunities.",
    },
  ];

  return (
    <section
      className={`relative py-16 px-4 ${
        isDarkMode ? "bg-[#000000]" : "bg-white"
      }`}
    >
      <div className="max-w-[1300px] mx-auto">
        {/* Header */}
        <div className="text-center mb-8 sm:mb-10 md:mb-12 lg:mb-16">
          <motion.h2
            className={`text-[20px] sm:text-[24px] md:text-[28px] lg:text-[36px] font-bold ${
              isDarkMode ? "text-[#fff]" : "text-[#000]"
            } font-lexend leading-tight px-4`}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            Reap the Benefits of Smart
            <br className="hidden sm:block" />
            Problem-Solving
          </motion.h2>
        </div>

        {/* Benefits Grid */}
        <div className="max-w-[280px] sm:max-w-[800px] lg:max-w-[1000px] mx-auto relative">
          {/* First Row - 3 cards */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-8 md:gap-10 mb-8 relative">
            {/* Vertical Gradient Lines - Only for tablet and up */}
            <div
              className="hidden sm:block absolute left-1/3 -translate-x-1/2 w-[2px] h-full"
              style={verticalGradientStyle}
            />
            <div
              className="hidden sm:block absolute left-2/3 -translate-x-1/2 w-[2px] h-full"
              style={verticalGradientStyle}
            />

            {benefits.slice(0, 3).map((benefit, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="w-full relative"
              >
                <BenefitCard {...benefit} />
                {/* Horizontal Gradient Line for Mobile */}
                {index < 2 && (
                  <div
                    className="block sm:hidden absolute left-0 right-0 bottom-[-1rem] h-[1px]"
                    style={horizontalGradientStyle}
                  />
                )}
              </motion.div>
            ))}
          </div>

          {/* Horizontal Gradient Line between rows */}
          <div
            className="block absolute left-0 right-0 h-[2px] -translate-y-6"
            style={horizontalGradientStyle}
          />

          {/* Second Row - 2 cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-8 md:gap-10 max-w-[280px] sm:max-w-[530px] md:max-w-[600px] mx-auto relative">
            {/* Vertical Gradient Line - Only for tablet and up */}
            <div
              className="hidden sm:block absolute left-1/2 -translate-x-1/2 w-[2px] h-full"
              style={verticalGradientStyle}
            />

            {benefits.slice(3).map((benefit, index) => (
              <motion.div
                key={index + 3}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: (index + 3) * 0.1 }}
                className="w-full relative"
              >
                <BenefitCard {...benefit} />
                {/* Horizontal Gradient Line for Mobile */}
                {index === 0 && (
                  <div
                    className="block sm:hidden absolute left-0 right-0 bottom-[-1rem] h-[1px]"
                    style={horizontalGradientStyle}
                  />
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default BenefitsSection;
